import * as THREE from "three";
import React, { memo, useEffect, useRef } from "react";
import { Line, Lines } from "./Line";
import { 
  calcCirclePoints, 
} from './utils';

/**
 * Generates spaced points along a Catmull-Rom curve defined by the input points.
 * @param {Array<THREE.Vector3>} vpoints - The input points to define the curve.
 * @param {boolean} close - Whether the curve should be closed or not.
 * @param {number} div - The number of divisions to generate along the curve.
 * @returns {Array<THREE.Vector3>} - The spaced points along the curve.
 * @throws {Error} - If the input points are null or include null values.
 */
export const MakeSplinePoints = (vpoints, close, div) => {
  if (vpoints == null) throw new Error("MakeSplinePoints:points are null");
  if (vpoints.length == 0) throw new Error("MakeSplinePoints:points are null");
  vpoints.forEach( pt => {
    if (pt == null) throw new Error("MakeSplinePoints:points include null");
  });

  var cnt = close ? vpoints.length * div : vpoints.length * div-2
  //var cnt = close ? div : div-1;
  const curve = new THREE.CatmullRomCurve3(vpoints, close, "catmullrom");
  
  return curve.getSpacedPoints(cnt);
}

/**
 * A memoized component that renders a spline shape.
 * @param {Array} vpoints - The points that define the spline.
 * @param {string} color - The color of the spline.
 * @param {number} lineWidth - The width of the spline line.
 * @param {boolean} close - Whether the spline should be closed.
 * @param {number} div - The number of divisions to use when creating the spline.
 * @returns {JSX.Element} - The rendered spline component.
 * @throws {Error} - If vpoints is null or empty.
 */
export const Spline = memo(({ vpoints, color, lineWidth, close, div }) => {
  // lineWidth doesn't working due to browser issues.
  if (vpoints == null) throw new Error("MakeSplinePoints:points are null");
  if (vpoints.length == 0) throw new Error("MakeSplinePoints:points are null");
  const vpoints2 = MakeSplinePoints(vpoints, close, div);

  /*
  return (
    <CatmullRomLine points={vpoints} lineWidth={lineWidth} closed={close} color={color} />
  );
  */

  return (
    <Line vpoints={vpoints2} color={color} />
  );
});

/**
 * A memoized component that renders a spline surface based on the given parameters.
 * @param {Object[]} vpoints - An array of Vector3 points that define the spline.
 * @param {number} width - The width of the spline surface.
 * @param {string} color - The color of the spline surface.
 * @param {boolean} close - Whether the spline should be closed or not.
 * @param {number} div - The number of divisions to use when creating the spline.
 * @param {string} dirSurface - The direction of the surface ('x', 'y', or 'z').
 * @param {boolean} spline - Whether to use a spline or not.
 * @returns {JSX.Element} - A JSX element that renders the spline surface.
 */
export const SplineSurface = memo(({ vpoints, width, color, close, div, dirSurface='x', spline=true}) => {
  var vpoints1 = [];
  var vpoints2 = [];
  vpoints.forEach(pt => {
    if (dirSurface === 'x') {
      vpoints1.push(new THREE.Vector3(pt.x+width, pt.y, pt.z));
      vpoints2.push(new THREE.Vector3(pt.x-width, pt.y, pt.z));
    } else if (dirSurface === 'y') {
      vpoints1.push(new THREE.Vector3(pt.x, pt.y+width, pt.z));
      vpoints2.push(new THREE.Vector3(pt.x, pt.y-width, pt.z));
    } else if (dirSurface === 'z') {
      vpoints1.push(new THREE.Vector3(pt.x, pt.y, pt.z+width));
      vpoints2.push(new THREE.Vector3(pt.x, pt.y, pt.z));
    }
  });

  if (spline) {
    var spoints1 = MakeSplinePoints(vpoints1, close, div);
    var spoints2 = MakeSplinePoints(vpoints2, close, div);
  } else {
    var spoints1 = vpoints1;
    var spoints2 = vpoints2;
  }
  var vlines = [];

  for (var i = 0; i < spoints1.length; i++) {
    var points = [];
    points.push(spoints1[i]);
    points.push(spoints2[i]);
    vlines.push(points);
  }

  return (
    <>
      <Lines vlines={vlines} color={color} />
      <Line vpoints={spoints1} color={color} />
      <Line vpoints={spoints2} color={color} />
    </>
  );
});

export function TubeCurve({vpoints, radius, color, nPoint=100, rotation=null, center=null, close=false, depthTest=true}) {
  const ref = useRef()
  const refGroup = useRef()
  if (vpoints[0] instanceof THREE.Vector3 === false) {
    vpoints = vpoints.map((point) => new THREE.Vector3(point.x, point.y, point.z));
  }

  var curve = new THREE.CatmullRomCurve3(vpoints, close)

  useEffect(() => {
    if (rotation != null) {
      ref.current.rotation.x = rotation.x;
      ref.current.rotation.y = rotation.y;
      ref.current.rotation.z = rotation.z;
    }
  }, [rotation])

  useEffect(() => {
    if (center != null) {
      refGroup.current.position.x = center.x;
      refGroup.current.position.y = center.y;
      refGroup.current.position.z = center.z;
    }
  }, [center])

  return (
    <group ref={refGroup}>
      <mesh ref={ref} renderOrder={1}>
        <tubeGeometry attach='geometry' args={[curve, nPoint, radius, 32]} />
        <meshBasicMaterial color={color} depthTest={depthTest} depthWrite={true}/>
      </mesh>
    </group>
  )
}

export function TubeCurveBase({vpoints, radius, color}) {
  const ref = useRef()
  const refGroup = useRef()

  var curve = new THREE.CatmullRomCurve3(vpoints)
  
  return (
    <mesh ref={ref}>
      <tubeGeometry attach='geometry' args={[curve, vpoints.length, radius, 32]} />
      <meshBasicMaterial color={color} />
    </mesh>
  )
}


export const CircleCurve = ({ center, radius, segments, color }) => {
  const points = calcCirclePoints(center, radius, segments);

  const geometry = React.useMemo(() => {
    return new THREE.BufferGeometry().setFromPoints(points);
  }, [points]);

  const material = React.useMemo(() => {
    return new THREE.LineBasicMaterial({ color });
  }, [color]);

  return <line geometry={geometry} material={material} />;
};